import { GraduationCap, ArrowUpRight, BadgeCheck, Clock } from "lucide-react";
import { Reveal, Eyebrow, scrollToId } from "./Reveal";
import { useLang } from "@/i18n";

const DOCTORS = [
    {
        key: "swapnil",
        testId: "doctor-card-dr-swapnil",
        enquiryId: "doctor-enquiry-dr-swapnil",
        photo: "/dr-swapnil.jpg",
        name: "Dr. Swapnil Bhagat",
        qualifications: "MBBS, DDV",
        lead: true,
    },
    {
        key: "seema",
        testId: "doctor-card-dr-seema",
        enquiryId: "doctor-enquiry-dr-seema",
        photo: "/dr-seema.jpg",
        name: "Dr. Seema Gupta",
        qualifications: "MBBS, MS ENT, DNB ENT",
        lead: false,
    },
];

export default function Doctors() {
    const { t } = useLang();

    const enquire = (key) => {
        window.dispatchEvent(new CustomEvent("prefill-doctor", { detail: key }));
        scrollToId("appointment");
    };

    return (
        <section id="doctors" className="relative py-24 md:py-32">
            <div className="mx-auto max-w-7xl px-5 md:px-8">
                <div className="mb-14 text-center">
                    <Reveal>
                        <Eyebrow>{t.doctors.eyebrow}</Eyebrow>
                        <h2 className="mx-auto mt-4 max-w-3xl font-display text-4xl font-semibold leading-[1.08] tracking-tight text-brand-navy md:text-5xl lg:text-6xl">
                            {t.doctors.h2a} <em className="italic text-brand-olive">{t.doctors.h2b}</em>
                        </h2>
                    </Reveal>
                </div>

                <div data-testid="doctors-showcase-section" className="grid grid-cols-1 gap-6 md:grid-cols-2 md:gap-8">
                    {DOCTORS.map((d, i) => {
                        const info = t.doctors[d.key];
                        return (
                            <Reveal key={d.key} delay={0.1 + i * 0.12} className="h-full">
                                <article
                                    data-testid={d.testId}
                                    className={`group relative flex h-full flex-col overflow-hidden rounded-3xl border p-8 transition-all duration-500 hover:-translate-y-1.5 md:p-10 ${
                                        d.lead
                                            ? "border-brand-accentline bg-brand-olivelight hover:shadow-lift"
                                            : "border-slate-200 bg-white shadow-card hover:border-brand-olive"
                                    }`}
                                >
                                    {d.lead && (
                                        <span className="absolute right-0 top-0 rounded-bl-2xl bg-brand-olive px-4 py-2 font-mono text-[10px] uppercase tracking-[0.18em] text-white">
                                            {t.doctors.lead}
                                        </span>
                                    )}

                                    <div className="flex items-center gap-5">
                                        <img
                                            src={d.photo}
                                            alt={`${d.name} — ${info.role}`}
                                            loading="lazy"
                                            className="h-20 w-20 shrink-0 rounded-2xl border border-white object-cover shadow-card md:h-24 md:w-24"
                                        />
                                        <div>
                                            <h3 className="font-display text-2xl font-semibold text-brand-navy md:text-3xl">{d.name}</h3>
                                            <p className="mt-1.5 flex items-center gap-2 font-mono text-xs text-brand-olive md:text-sm">
                                                <GraduationCap className="h-4 w-4" />
                                                {d.qualifications}
                                            </p>
                                        </div>
                                    </div>

                                    <p className="mt-6 inline-flex w-fit items-center gap-2 rounded-full bg-white px-4 py-2 text-xs font-bold text-brand-navy shadow-sm md:text-sm">
                                        <BadgeCheck className="h-4 w-4 text-brand-lime" />
                                        {info.role}
                                    </p>

                                    <ul className="mt-6 space-y-3">
                                        {info.focus.map((f) => (
                                            <li key={f} className="flex items-start gap-3 text-sm text-slate-600">
                                                <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-brand-lime" />
                                                {f}
                                            </li>
                                        ))}
                                    </ul>

                                    <div
                                        data-testid={`${d.testId}-timings`}
                                        className="mt-6 rounded-2xl border border-slate-200/80 bg-white/70 p-4 md:p-5"
                                    >
                                        <p className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-brand-olive">
                                            <Clock className="h-3.5 w-3.5" />
                                            {t.doctors.hours}
                                        </p>
                                        <div className="mt-3 space-y-1.5">
                                            {info.timings.map((tm) => (
                                                <div key={tm.d} className="flex flex-wrap items-baseline justify-between gap-x-3 text-sm">
                                                    <span className="font-semibold text-brand-navy">{tm.d}</span>
                                                    <span className={tm.closed ? "font-semibold text-red-500" : "text-slate-600"}>{tm.h}</span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>

                                    <div className="mt-auto flex items-center justify-between border-t border-slate-200/80 pt-6">
                                        <p className="mt-6 font-mono text-[11px] uppercase tracking-[0.18em] text-slate-400 md:mt-0">
                                            {t.doctors.consultNote}
                                        </p>
                                        <button
                                            data-testid={d.enquiryId}
                                            onClick={() => enquire(d.key)}
                                            className="mt-6 inline-flex items-center gap-2 rounded-full bg-brand-navy px-5 py-2.5 text-xs font-semibold text-white transition-all duration-300 hover:bg-brand-olive md:mt-0 md:text-sm"
                                        >
                                            {t.doctors.enquire}
                                            <ArrowUpRight className="h-4 w-4" />
                                        </button>
                                    </div>
                                </article>
                            </Reveal>
                        );
                    })}
                </div>
            </div>
        </section>
    );
}
